import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
import { previewPages } from '../../data/previewPages';
import { padNumber } from '../../utils/format';
type PageLightboxProps = {
  index: number;
  onChange: (index: number) => void;
  onClose: () => void;
};
export function PageLightbox({
  index,
  onChange,
  onClose
}: PageLightboxProps) {
  const [zoomed, setZoomed] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const page = previewPages[index];
  const total = previewPages.length;
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus();
    };
  }, []);
  useEffect(() => {
    setZoomed(false);
  }, [index]);
  const go = (step: number) => onChange((index + step + total) % total);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowRight') {
      go(1);
    } else if (event.key === 'ArrowLeft') {
      go(-1);
    } else if (event.key === 'Tab' && dialogRef.current) {
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>('button');
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
  const controlClass = 'inline-flex h-12 min-w-[48px] items-center justify-center gap-2 border-2 border-gold px-3 font-bold text-gold transition-colors duration-150 hover:bg-gold hover:text-ink';
  return <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="lightbox-title" onKeyDown={handleKeyDown} className="fixed inset-0 z-[60] flex flex-col bg-black/95 text-white" data-id="element-98" data-mp="3m4lwnx53el9d">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-6" data-id="element-99" data-mp="3vuoohu0h9t64">
        <p id="lightbox-title" className="min-w-0 truncate text-base font-bold" data-id="element-100" data-mp="4p5cepuk7rw3">
          Page {page.page} · {page.title}
        </p>
        <div className="flex shrink-0 items-center gap-2" data-id="element-101" data-mp="dn8zvrp3uyqj">
          <button type="button" onClick={() => setZoomed(value => !value)} aria-pressed={zoomed} className={controlClass} data-id="element-102" data-mp="dlo3rxn577qk">
            {zoomed ? <ZoomOutIcon aria-hidden="true" className="h-5 w-5" data-id="element-103" data-mp="2fgyl6ph6n8xj" /> : <ZoomInIcon aria-hidden="true" className="h-5 w-5" data-id="element-104" data-mp="2kdf7de4tnd7z" />}
            <span className="hidden sm:inline" data-id="element-105" data-mp="2cupdyw2ensub">{zoomed ? 'Fit to screen' : 'Zoom in'}</span>
            <span className="sr-only sm:hidden" data-id="element-106" data-mp="2cwipaonyr18n">{zoomed ? 'Fit to screen' : 'Zoom in'}</span>
          </button>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="Close preview" className={controlClass} data-id="element-107" data-mp="234w5i3xk7tua">
            <XIcon aria-hidden="true" className="h-5 w-5" data-id="element-108" data-mp="1y6bcopkn0cfv" />
          </button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto" data-id="element-109" data-mp="2vttm82m4hikx">
        {zoomed ? <div className="p-3 sm:p-6" data-id="element-110" data-mp="2qugx1b7s3zf6">
            <img src={page.src} alt={`Page ${page.page} of the guide: ${page.title}, enlarged`} className="mx-auto block h-auto w-[220%] max-w-none sm:w-full sm:max-w-[1152px]" data-id="element-111" data-mp="2qvi99j0j6ryh" />
          </div> : <div className="flex h-full items-center justify-center p-3 sm:p-6" data-id="element-112" data-mp="1afyyiwystjgu">
            <img src={page.src} alt={`Page ${page.page} of the guide: ${page.title}`} className="block h-auto max-h-full w-auto max-w-full" data-id="element-113" data-mp="1adukvnb3l63d" />
          </div>}
      </div>

      <div className="flex items-center justify-center gap-4 border-t border-white/10 px-4 py-3" data-id="element-114" data-mp="2z2jtjyi0wlf1">
        <button type="button" onClick={() => go(-1)} aria-label="Previous page" className={controlClass} data-id="element-115" data-mp="2kdnjjvweoybs">
          <ChevronLeftIcon aria-hidden="true" className="h-6 w-6" data-id="element-116" data-mp="2pbfe6xofqyjo" />
        </button>
        <p className="min-w-[72px] text-center font-display text-2xl font-bold tabular-nums" aria-live="polite" data-id="element-117" data-mp="3d22p7rtne4ag">
          {padNumber(index + 1)} / {padNumber(total)}
        </p>
        <button type="button" onClick={() => go(1)} aria-label="Next page" className={controlClass} data-id="element-118" data-mp="3hyjjl3rfxevz">
          <ChevronRightIcon aria-hidden="true" className="h-6 w-6" data-id="element-119" data-mp="382q1l4uyct6t" />
        </button>
      </div>
    </div>;
}